import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { DeviceSummary } from "@kidtube/shared";
import { api, type Schedule, type ScheduleInput } from "../api";
import { EmptyState } from "../components/Feedback";
import { formatDateTime } from "../utils";

interface SettingsScreenProps {
  showSuccess: (message: string) => void;
  showError: (error: unknown) => void;
}

interface DeviceDraft {
  name: string;
  dailyLimitMinutes: string;
}

interface ScheduleDraft extends ScheduleInput {
  localId: string;
}

const DAY_NAMES = ["Chủ nhật", "Thứ hai", "Thứ ba", "Thứ tư", "Thứ năm", "Thứ sáu", "Thứ bảy"];

function minuteToTime(value: number): string {
  return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
}

function timeToMinute(value: string): number {
  const [hours, minutes] = value.split(":").map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

function scheduleToDraft(schedule: Schedule): ScheduleDraft {
  return {
    localId: schedule.id,
    dayOfWeek: schedule.day_of_week,
    startMinute: schedule.start_minute,
    endMinute: schedule.end_minute,
    enabled: schedule.enabled === 1,
  };
}

export function SettingsScreen({ showSuccess, showError }: SettingsScreenProps) {
  const [devices, setDevices] = useState<DeviceSummary[]>([]);
  const [drafts, setDrafts] = useState<Record<string, DeviceDraft>>({});
  const [pairingCode, setPairingCode] = useState("");
  const [pairingName, setPairingName] = useState("");
  const [scheduleDeviceId, setScheduleDeviceId] = useState<string | null>(null);
  const [scheduleDrafts, setScheduleDrafts] = useState<ScheduleDraft[]>([]);
  const [sessions, setSessions] = useState<Array<Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [deviceList, sessionList] = await Promise.all([api.devices(), api.sessions()]);
      setDevices(deviceList);
      setSessions(sessionList);
      setDrafts(
        Object.fromEntries(
          deviceList.map((device) => [
            device.id,
            {
              name: device.name,
              dailyLimitMinutes:
                device.dailyLimitMinutes === null ? "" : String(device.dailyLimitMinutes),
            },
          ]),
        ),
      );
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load]);

  async function pairDevice(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!/^\d{6}$/.test(pairingCode)) return;
    setBusy("pair");
    try {
      await api.claimPairing(pairingCode, pairingName.trim());
      setPairingCode("");
      setPairingName("");
      await load();
      showSuccess("Đã liên kết TV với KidTube.");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  }

  function updateDraft(deviceId: string, update: Partial<DeviceDraft>) {
    setDrafts((current) => ({
      ...current,
      [deviceId]: {
        name: current[deviceId]?.name ?? "",
        dailyLimitMinutes: current[deviceId]?.dailyLimitMinutes ?? "",
        ...update,
      },
    }));
  }

  async function saveDevice(device: DeviceSummary) {
    const draft = drafts[device.id];
    if (draft === undefined || draft.name.trim().length === 0) return;
    const limit = draft.dailyLimitMinutes.trim();
    const parsedLimit = limit === "" ? null : Number.parseInt(limit, 10);
    if (parsedLimit !== null && (!Number.isInteger(parsedLimit) || parsedLimit < 1 || parsedLimit > 1440)) {
      showError(new Error("Giới hạn ngày phải từ 1 đến 1440 phút hoặc để trống."));
      return;
    }
    setBusy(device.id);
    try {
      await api.updateDevice(device.id, {
        name: draft.name.trim(),
        dailyLimitMinutes: parsedLimit,
      });
      await load();
      showSuccess(`Đã lưu cấu hình cho ${draft.name.trim()}.`);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  }

  async function revokeDevice(device: DeviceSummary) {
    if (
      !window.confirm(
        `Thu hồi “${device.name}”? TV sẽ phải tạo mã mới và liên kết lại để dùng KidTube.`,
      )
    ) return;
    setBusy(device.id);
    try {
      await api.revokeDevice(device.id);
      await load();
      showSuccess("Đã thu hồi liên kết thiết bị.");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  }

  async function openSchedule(device: DeviceSummary) {
    setBusy(`${device.id}:schedule-load`);
    try {
      const values = await api.schedules(device.id);
      setScheduleDrafts(values.map(scheduleToDraft));
      setScheduleDeviceId(device.id);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  }

  function addSchedule() {
    setScheduleDrafts((current) => [
      ...current,
      {
        localId: crypto.randomUUID(),
        dayOfWeek: 1,
        startMinute: 17 * 60 + 30,
        endMinute: 18 * 60 + 30,
        enabled: true,
      },
    ]);
  }

  function updateSchedule(localId: string, update: Partial<ScheduleDraft>) {
    setScheduleDrafts((current) =>
      current.map((schedule) =>
        schedule.localId === localId ? { ...schedule, ...update } : schedule,
      ),
    );
  }

  async function saveSchedules() {
    if (scheduleDeviceId === null) return;
    const invalid = scheduleDrafts.some(
      (schedule) =>
        schedule.dayOfWeek < 0 ||
        schedule.dayOfWeek > 6 ||
        schedule.startMinute < 0 ||
        schedule.endMinute > 1440 ||
        schedule.endMinute <= schedule.startMinute,
    );
    if (invalid) {
      showError(new Error("Mỗi khung giờ phải có giờ kết thúc sau giờ bắt đầu."));
      return;
    }
    setBusy("schedule-save");
    try {
      await api.replaceSchedules(
        scheduleDeviceId,
        scheduleDrafts.map(({ dayOfWeek, startMinute, endMinute, enabled }) => ({
          dayOfWeek,
          startMinute,
          endMinute,
          enabled,
        })),
      );
      setScheduleDeviceId(null);
      showSuccess("Đã lưu lịch xem cho TV.");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  }

  async function revokeSession(session: Record<string, unknown>) {
    const id = typeof session.id === "string" ? session.id : null;
    if (id === null || session.current === true) return;
    setBusy(id);
    try {
      await api.revokeSession(id);
      setSessions((current) => current.filter((item) => item.id !== id));
      showSuccess("Đã thu hồi phiên đăng nhập.");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  }

  const scheduleDevice = devices.find((device) => device.id === scheduleDeviceId) ?? null;
  const tvCount = devices.filter((device) => device.deviceKind === "TV").length;
  const webChildCount = devices.filter((device) => device.deviceKind === "WEB_CHILD").length;
  const canPair = tvCount < 2 || webChildCount < 1;

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <p className="eyebrow">DEVICES &amp; SECURITY</p>
          <h1>Thiết bị và cài đặt</h1>
          <p>Liên kết tối đa hai TV và một Child Web cho iPhone; cùng dùng giới hạn ngày, lịch xem và block rule.</p>
        </div>
      </header>

      {canPair && (
        <section className="settings-card pairing-card">
          <div className="settings-card-heading">
            <span className="settings-icon">＋</span>
            <div><h2>Liên kết thiết bị mới</h2><p>Mở KidTube trên TV hoặc Child Web và nhập mã 6 số đang hiển thị.</p></div>
          </div>
          <form className="pairing-form" onSubmit={pairDevice}>
            <label>
              Mã liên kết
              <input
                className="pairing-code-input"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={pairingCode}
                onChange={(event) => setPairingCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="482931"
                required
              />
            </label>
            <label>
              Tên thiết bị (không bắt buộc)
              <input
                value={pairingName}
                onChange={(event) => setPairingName(event.target.value)}
                placeholder="Ví dụ: iPhone của con"
                maxLength={80}
              />
            </label>
            <button type="submit" className="primary-button" disabled={busy !== null || pairingCode.length !== 6}>
              {busy === "pair" ? "Đang liên kết…" : "Liên kết thiết bị"}
            </button>
          </form>
        </section>
      )}

      <section className="section-block">
        <div className="section-heading"><div><h2>Thiết bị đã liên kết</h2><p>{tvCount}/2 TV · {webChildCount}/1 Child Web.</p></div></div>
        {loading && devices.length === 0 ? (
          <div className="skeleton skeleton-section" />
        ) : devices.length === 0 ? (
          <EmptyState icon="▣" title="Chưa có thiết bị" description="Mở KidTube trên TV hoặc Child Web để bắt đầu pairing." />
        ) : (
          <div className="settings-device-list">
            {devices.map((device) => {
              const draft = drafts[device.id] ?? {
                name: device.name,
                dailyLimitMinutes:
                  device.dailyLimitMinutes === null ? "" : String(device.dailyLimitMinutes),
              };
              return (
                <article className="settings-device" key={device.id}>
                  <header>
                    <span className={`device-platform ${device.deviceKind === "WEB_CHILD" ? "web" : device.platform === "SAMSUNG_TIZEN" ? "samsung" : "android"}`}>
                      {device.deviceKind === "WEB_CHILD" ? "W" : device.platform === "SAMSUNG_TIZEN" ? "TV" : "A"}
                    </span>
                    <div><h3>{device.name}</h3><p>{device.model} · {device.osVersion}</p></div>
                    <span className={`status-pill ${device.online ? "online" : "offline"}`}>{device.online ? "Online" : "Offline"}</span>
                  </header>
                  <div className="settings-fields">
                    <label>
                      Tên hiển thị
                      <input value={draft.name} maxLength={80} onChange={(event) => updateDraft(device.id, { name: event.target.value })} />
                    </label>
                    <label>
                      Giới hạn mỗi ngày (phút)
                      <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={1440}
                        value={draft.dailyLimitMinutes}
                        onChange={(event) => updateDraft(device.id, { dailyLimitMinutes: event.target.value })}
                        placeholder="Không giới hạn"
                      />
                    </label>
                  </div>
                  <footer>
                    <button type="button" className="primary-button" onClick={() => void saveDevice(device)} disabled={busy !== null}>Lưu cấu hình</button>
                    <button type="button" className="secondary-button" onClick={() => void openSchedule(device)} disabled={busy !== null}>◷ Lịch xem</button>
                    <button type="button" className="secondary-button danger-text push-right" onClick={() => void revokeDevice(device)} disabled={busy !== null}>Thu hồi thiết bị</button>
                  </footer>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="settings-card">
        <div className="settings-card-heading">
          <span className="settings-icon">⌾</span>
          <div><h2>Phiên đăng nhập Parent</h2><p>Thu hồi những phiên không còn sử dụng.</p></div>
        </div>
        <div className="session-list">
          {sessions.map((session) => {
            const id = String(session.id ?? "");
            const current = session.current === true;
            return (
              <div className="session-row" key={id}>
                <span className="session-dot" />
                <div><strong>{current ? "Thiết bị hiện tại" : "Phiên Parent"}</strong><small>Hoạt động: {formatDateTime(typeof session.last_seen_at === "string" ? session.last_seen_at : null)} · Hết hạn: {formatDateTime(typeof session.expires_at === "string" ? session.expires_at : null)}</small></div>
                {current ? <span className="status-pill online">Hiện tại</span> : <button type="button" className="danger-text" onClick={() => void revokeSession(session)} disabled={busy !== null}>Thu hồi</button>}
              </div>
            );
          })}
        </div>
      </section>

      {scheduleDevice !== null && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setScheduleDeviceId(null);
        }}>
          <section className="modal-card schedule-modal" role="dialog" aria-modal="true" aria-labelledby="schedule-title">
            <header>
              <div><p className="eyebrow">WATCH SCHEDULE</p><h2 id="schedule-title">Lịch xem · {scheduleDevice.name}</h2></div>
              <button type="button" className="icon-button" onClick={() => setScheduleDeviceId(null)} aria-label="Đóng">×</button>
            </header>
            <p className="modal-description">Nếu không có khung giờ nào, TV được phép xem bất cứ lúc nào. Ngoài các khung đã đặt, trẻ thấy “Hiện chưa đến giờ xem.”</p>
            <div className="schedule-list">
              {scheduleDrafts.map((schedule) => (
                <div className="schedule-row" key={schedule.localId}>
                  <select value={schedule.dayOfWeek} onChange={(event) => updateSchedule(schedule.localId, { dayOfWeek: Number(event.target.value) })} aria-label="Ngày trong tuần">
                    {DAY_NAMES.map((day, index) => <option key={day} value={index}>{day}</option>)}
                  </select>
                  <input type="time" value={minuteToTime(schedule.startMinute)} onChange={(event) => updateSchedule(schedule.localId, { startMinute: timeToMinute(event.target.value) })} aria-label="Giờ bắt đầu" />
                  <span>→</span>
                  <input type="time" value={minuteToTime(schedule.endMinute)} onChange={(event) => updateSchedule(schedule.localId, { endMinute: timeToMinute(event.target.value) })} aria-label="Giờ kết thúc" />
                  <label className="switch" title="Bật hoặc tắt khung giờ"><input type="checkbox" checked={schedule.enabled} onChange={(event) => updateSchedule(schedule.localId, { enabled: event.target.checked })} /><span /></label>
                  <button type="button" className="icon-button danger-text" onClick={() => setScheduleDrafts((current) => current.filter((item) => item.localId !== schedule.localId))} aria-label="Xóa khung giờ">×</button>
                </div>
              ))}
              {scheduleDrafts.length === 0 && <div className="section-empty">Chưa giới hạn lịch. TV có thể xem bất cứ lúc nào trong giới hạn ngày.</div>}
            </div>
            <button type="button" className="secondary-button" onClick={addSchedule}>+ Thêm khung giờ</button>
            <footer>
              <button type="button" className="secondary-button" onClick={() => setScheduleDeviceId(null)}>Hủy</button>
              <button type="button" className="primary-button" onClick={() => void saveSchedules()} disabled={busy !== null}>{busy === "schedule-save" ? "Đang lưu…" : "Lưu lịch xem"}</button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}
